import { useEffect, useState } from 'react';
import {
  addIngredient,
  deleteIngredient,
  listIngredients,
  updateIngredient,
} from '../../lib/ingredients';
import { formatJMD } from '../../lib/finances';
import { SearchIcon } from '../icons';
import { useConfirm } from '../../components/ConfirmDialog';

const BLANK = { name: '', inci_name: '', cost_per_kg: '', max_use_pct: '', supplier: '', notes: '' };

// The shared raw-material list. Purchased bases (body cream base, melt & pour
// soap base) live here alongside butters and fragrances — anything you buy is
// an ingredient. Pricing one entry reprices every formula that uses it, which
// is the propagation that actually matters when a supplier changes price.
export default function IngredientLibrary({ onChanged, formulas = [] }) {
  const confirm = useConfirm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [form, setForm] = useState(BLANK);
  const [editingId, setEditingId] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [unpricedOnly, setUnpricedOnly] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setRows(await listIngredients());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const startEdit = (row) => {
    setEditingId(row.id);
    setForm({
      name: row.name || '',
      inci_name: row.inci_name || '',
      cost_per_kg: row.cost_per_kg ?? '',
      max_use_pct: row.max_use_pct ?? '',
      supplier: row.supplier || '',
      notes: row.notes || '',
    });
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: form.name.trim(),
        inci_name: form.inci_name.trim() || null,
        // Empty means "not priced yet", which is different from zero — a
        // real zero would claim the material is free.
        cost_per_kg: form.cost_per_kg === '' ? null : Number(form.cost_per_kg),
        // A cap, when there is one. Blank means uncapped, not zero — a
        // variant override is checked against this at batch time.
        max_use_pct: form.max_use_pct === '' ? null : Number(form.max_use_pct),
        supplier: form.supplier.trim() || null,
        notes: form.notes.trim() || null,
      };
      if (editingId) await updateIngredient(editingId, payload);
      else await addIngredient(payload);
      setForm(BLANK);
      setEditingId(null);
      setOpen(false);
      await load();
      onChanged && onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Which formulas' current versions use this ingredient. Counted from
  // what the page already has rather than a per-row query.
  const usedIn = (ingredientId) =>
    formulas.filter((f) =>
      (f.currentVersion?.formula_ingredients || []).some((l) => l.ingredient_id === ingredientId)
    );

  const handleDelete = async (row) => {
    const id = row.id;
    try {
      const users = usedIn(id);
      // Deleting one out from under a formula would strip its price and,
      // worse, break any variant override keyed to it.
      if (users.length) {
        if (
          !(await confirm({
            title: `${row.name} is used by ${users.length} formula${users.length === 1 ? '' : 's'}`,
            body: `${users.map((f) => f.name).join(', ')} still use it, so it can't be deleted. Archive it instead — it stays available to those formulas but stops appearing when you add new lines.`,
            confirmLabel: 'Archive it',
          }))
        )
          return;
        await updateIngredient(id, { is_archived: true });
        await load();
        onChanged && onChanged();
        return;
      }

      if (!(await confirm({
        title: 'Delete this ingredient?',
        body: "Formulas using it fall back to their own stored price, if they have one.",
      }))) return;

      await deleteIngredient(id);
      await load();
      onChanged && onChanged();
    } catch (err) {
      setError(err.message);
    }
  };

  const filtered = rows.filter((r) => {
    if (unpricedOnly && Number(r.cost_per_kg) > 0) return false;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return `${r.name} ${r.inci_name || ''} ${r.supplier || ''}`.toLowerCase().includes(q);
  });

  const unpriced = rows.filter((r) => !(Number(r.cost_per_kg) > 0)).length;

  return (
    <section className="panel">
      <div className="library-toolbar">
        <div className="library-search">
          <SearchIcon />
          <input
            type="search"
            placeholder="Search ingredients…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        {!open && (
          <button
            className="primary-action"
            onClick={() => {
              setForm(BLANK);
              setEditingId(null);
              setOpen(true);
            }}
          >
            + Add ingredient
          </button>
        )}
      </div>

      {rows.length > 0 && unpriced > 0 && (
        /* A count, not a banner. The yellow warning shouted on every visit
           while nothing was priced, which made it furniture rather than
           information. */
        <button
          className={'ingredient-unpriced' + (unpricedOnly ? ' active' : '')}
          onClick={() => setUnpricedOnly((v) => !v)}
        >
          {unpriced} unpriced
        </button>
      )}

      {error && <p className="formula-error">{error}</p>}

      {open && (
        <form className="ingredient-form" onSubmit={handleSubmit}>
          <div className="ingredient-form-grid">
            <label>
              Ingredient name
              <input required value={form.name} onChange={set('name')} placeholder="e.g. Body cream base" />
            </label>
            <label>
              INCI name (optional)
              <input value={form.inci_name} onChange={set('inci_name')} />
            </label>
            <label>
              Cost per kg (JMD)
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.cost_per_kg}
                onChange={set('cost_per_kg')}
                placeholder="Leave blank if unknown"
              />
            </label>
            <label>
              Max use %
              <input
                type="number"
                step="any"
                min="0"
                max="100"
                value={form.max_use_pct}
                onChange={set('max_use_pct')}
                placeholder="Blank if uncapped"
              />
            </label>
            <label>
              Supplier (optional)
              <input value={form.supplier} onChange={set('supplier')} />
            </label>
            <label className="ingredient-form-wide">
              Notes (optional)
              <input value={form.notes} onChange={set('notes')} />
            </label>
          </div>
          <div className="ingredient-form-actions">
            <button
              type="button"
              className="ghost-button"
              onClick={() => {
                setOpen(false);
                setEditingId(null);
              }}
            >
              Cancel
            </button>
            <button type="submit" className="primary-action" disabled={saving || !form.name.trim()}>
              {saving ? 'Saving…' : editingId ? 'Save changes' : 'Add ingredient'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="formula-loading">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="formula-list-empty">
          {rows.length === 0
            ? 'No ingredients yet — add the raw materials and bases you buy.'
            : 'No ingredients match that search.'}
        </p>
      ) : (
        <div className="ingredient-table-wrap">
          <table className="ingredient-table">
            <thead>
              <tr>
                <th>Ingredient</th>
                <th>INCI</th>
                <th>Supplier</th>
                <th className="numeric">Max use</th>
                <th className="numeric">Used in</th>
                <th className="numeric">Cost / kg</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.id}>
                  <td>
                    <span className="ingredient-name">{row.name}</span>
                    {row.notes && <span className="ingredient-note">{row.notes}</span>}
                  </td>
                  <td className="muted">{row.inci_name || '—'}</td>
                  <td className="muted">{row.supplier || '—'}</td>
                  <td className="numeric muted">
                    {row.max_use_pct != null ? `${row.max_use_pct}%` : '—'}
                  </td>
                  <td className="numeric muted">{usedIn(row.id).length || '—'}</td>
                  <td className="numeric">
                    {Number(row.cost_per_kg) > 0 ? (
                      formatJMD(row.cost_per_kg)
                    ) : (
                      <span className="cost-badge cost-badge-unknown">Not priced</span>
                    )}
                  </td>
                  <td className="ingredient-actions">
                    <button onClick={() => startEdit(row)}>Edit</button>
                    <button className="danger" onClick={() => handleDelete(row)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
